import { makeStyles } from '@material-ui/core/styles';
import {
    ContainerFieldsList,
    CustomSelectArrayInput,
    CustomSelectInput,
} from 'candy-commons-components';
import React from 'react';
import {
    ArrayInput,
    BooleanInput,
    DateInput,
    FormDataConsumer,
    SelectArrayInput,
    SimpleFormIterator,
    TextInput,
    required,
} from 'react-admin';
import { useSelector } from 'react-redux';
import COUNTRIES from '../../data/countries.json';
import PAGE_CONTENTS from '../../data/newFeaturesBannerPageOptions.json';
import { getApplianceTypes, getBrands } from '../../selectors/cmsConfig';

const countryChoices = COUNTRIES.map((country) =>
    country.id === 'ALL' ? { ...country, name: 'All countries' } : country
);
const requiredField = required();
const validateCountries = [
    requiredField,
    (countries) =>
        countries?.includes('ALL') && countries.length > 1
            ? 'Select All countries on its own.'
            : undefined,
];

export const prepareBannerFormData = ({ deeplink, linkRules, ...banner }) =>
    banner.pageContent === 'HOMEPAGE'
        ? { ...banner, linkRules: linkRules ?? [] }
        : { ...banner, deeplink };

const useStyles = makeStyles((theme) => ({
    linkRows: {
        '& > li > section': {
            display: 'flex',
            columnGap: theme.spacing(2),
            minWidth: 0,
            '& > div': {
                flex: '1 1 0',
                maxWidth: 400,
                minWidth: 0,
            },
        },
    },
}));

const NewFeaturesBannerFields = ({ disabled = false, ...inputProps }) => {
    const classes = useStyles();
    const applianceTypes = useSelector(getApplianceTypes);
    const brands = useSelector(getBrands);

    return (
        <>
            <ContainerFieldsList {...inputProps}>
                <TextInput
                    label="Title"
                    source="title"
                    validate={requiredField}
                    disabled={disabled}
                />
                <TextInput
                    label="Description"
                    source="description"
                    disabled={disabled}
                />
                <CustomSelectInput
                    label="Page content"
                    source="pageContent"
                    validate={requiredField}
                    choices={PAGE_CONTENTS}
                    disabled={disabled}
                />
                <DateInput
                    source="validFrom"
                    label="Valid from"
                    validate={requiredField}
                    disabled={disabled}
                />
                <DateInput
                    source="validTo"
                    label="Valid to"
                    validate={requiredField}
                    disabled={disabled}
                />
                <TextInput
                    label="Image 24x24"
                    source="image"
                    disabled={disabled}
                />
                <FormDataConsumer>
                    {({ formData, ...props }) =>
                        formData.pageContent !== 'HOMEPAGE' && (
                            <TextInput
                                {...props}
                                label="Deeplink"
                                source="deeplink"
                                disabled={disabled}
                            />
                        )
                    }
                </FormDataConsumer>
            </ContainerFieldsList>
            <FormDataConsumer {...inputProps}>
                {({ formData, ...props }) =>
                    formData.pageContent === 'HOMEPAGE' && (
                        <ArrayInput
                            {...props}
                            source="linkRules"
                            label="Deeplinks by country"
                            helperText="Use All countries for countries without a specific deeplink."
                            disabled={disabled}
                        >
                            <SimpleFormIterator
                                className={classes.linkRows}
                                disableReordering
                            >
                                <SelectArrayInput
                                    source="countries"
                                    label="Countries"
                                    choices={countryChoices}
                                    translateChoice={false}
                                    validate={validateCountries}
                                    fullWidth
                                />
                                <TextInput
                                    source="deeplink"
                                    label="Deeplink"
                                    validate={requiredField}
                                    fullWidth
                                />
                            </SimpleFormIterator>
                        </ArrayInput>
                    )
                }
            </FormDataConsumer>
            <FormDataConsumer {...inputProps}>
                {({ formData, ...props }) => {
                    if (
                        formData.pageContent === 'ECOMMERCE_HOMEPAGE' ||
                        formData.pageContent === 'ECOMMERCE_HOMEPAGE_2'
                    ) {
                        return null;
                    }

                    return (
                        <>
                            <ContainerFieldsList {...props}>
                                <FormDataConsumer>
                                    {({ formData, ...props }) =>
                                        !formData.isAllAppliance && (
                                            <CustomSelectArrayInput
                                                {...props}
                                                label="Appliance types"
                                                source="applianceType"
                                                choices={applianceTypes}
                                                validate={requiredField}
                                                disabled={disabled}
                                            />
                                        )
                                    }
                                </FormDataConsumer>
                                <BooleanInput
                                    label="All appliance"
                                    source="isAllAppliance"
                                    defaultValue={false}
                                    disabled={disabled}
                                />
                            </ContainerFieldsList>
                            <ContainerFieldsList {...props}>
                                <FormDataConsumer>
                                    {({ formData, ...props }) =>
                                        !formData.isAllBrand && (
                                            <CustomSelectArrayInput
                                                {...props}
                                                label="Brand"
                                                source="brand"
                                                choices={brands.filter(
                                                    (brand) =>
                                                        brand.id !== 'all'
                                                )}
                                                validate={requiredField}
                                                disabled={disabled}
                                            />
                                        )
                                    }
                                </FormDataConsumer>
                                <BooleanInput
                                    label="All brand"
                                    source="isAllBrand"
                                    defaultValue={false}
                                    disabled={disabled}
                                />
                            </ContainerFieldsList>
                        </>
                    );
                }}
            </FormDataConsumer>
        </>
    );
};

NewFeaturesBannerFields.defaultProps = {
    fullWidth: true,
};

export default NewFeaturesBannerFields;
